import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, EnvelopeSimple, Flame } from "@phosphor-icons/react";

import api from "../lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!email.trim()) {
      setError("Introduce tu correo electrónico.");
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
      });

      setSuccess(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;

      setError(
        typeof detail === "string"
          ? detail
          : "No se pudo solicitar la recuperación de contraseña.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2">
      {/* IMAGEN LATERAL */}
      <div
        className="relative hidden lg:block overflow-hidden"
        style={{
          backgroundImage: 'url("/login.png")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/10 to-med-primary/30" />

        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.3em] mb-3 opacity-80">
            Fiesta · Tradición · Comunidad
          </div>

          <h2 className="font-display text-4xl xl:text-5xl font-bold leading-tight max-w-md">
            Tu falla, organizada como nunca.
          </h2>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="flex items-center justify-center p-8 bg-med-bg">
        <div className="w-full max-w-md">
          {/* CABECERA */}
          <div className="flex items-center gap-4 mb-10">
            <div className="w-10 h-10 rounded-xl overflow-hidden grid place-items-center">
              <img
                src="/favicon.png"
                alt="Trakka"
                className="w-full h-full object-contain"
              />
            </div>

            <div>
              <div className="font-display text-2xl font-bold text-med-text">
                Trakka
              </div>

              <div className="text-xs uppercase tracking-[0.2em] text-med-muted">
                Panel de administración
              </div>
            </div>
          </div>

          {success ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-med-primary/10 text-med-primary grid place-items-center mb-6">
                <EnvelopeSimple size={28} weight="duotone" />
              </div>

              <h1 className="font-display text-4xl sm:text-5xl font-bold text-med-text mb-3">
                Revisa tu correo
              </h1>

              <p className="text-med-muted leading-7 mb-8">
                Si existe una cuenta asociada a este correo, te hemos enviado un
                enlace para recuperar tu contraseña.
              </p>

              <div className="bg-med-surface2 rounded-xl px-4 py-4 text-sm text-med-muted mb-8">
                Si no recibes nada en unos minutos, revisa la carpeta de spam o
                comprueba que sea el correo con el que te registraste.
              </div>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 font-semibold transition-colors active:scale-[0.98]"
              >
                Volver a iniciar sesión
              </button>
            </>
          ) : (
            <>
              <h1 className="font-display text-4xl sm:text-5xl font-bold text-med-text mb-2">
                Recuperar contraseña
              </h1>

              <p className="text-med-muted mb-10">
                Introduce el correo asociado a tu cuenta y te enviaremos un
                enlace para crear una nueva contraseña.
              </p>

              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-med-text mb-2">
                    Email
                  </label>

                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 rounded-xl px-4 py-3 pr-12 outline-none transition-all"
                      placeholder="nombre@correo.com"
                      autoComplete="email"
                      autoFocus
                    />

                    <EnvelopeSimple
                      size={20}
                      weight="duotone"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-med-muted"
                    />
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-med-danger bg-med-danger/10 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 font-semibold transition-colors active:scale-[0.98] disabled:opacity-60"
                >
                  {submitting ? "Enviando…" : "Enviar enlace de recuperación"}
                </button>
              </form>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mt-6 flex items-center gap-2 text-sm text-med-muted hover:text-med-primary transition-colors"
              >
                <ArrowLeft size={18} weight="bold" />
                Volver a iniciar sesión
              </button>
            </>
          )}

          <div className="mt-10 text-xs text-med-muted">
            Sistema interno de gestión de la falla.
          </div>
        </div>
      </div>
    </div>
  );
}
